import { ArrowBackIosRounded, Refresh } from '@mui/icons-material'
import { Divider, TextField } from '@mui/material'
import { HEADER_HEIGHT, theme } from '../../helpers/theme'
import { fetchColumnLineage } from '../../store/actionCreators'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import MQTooltip from '../../components/core/tooltip/MQTooltip'
import MqText from '../../components/core/text/MqText'
import React from 'react'
import { useSelector } from 'react-redux'
import { IState } from '../../store/reducers'
import { iframeLightColor, borderWhite, iframeLightBlockTitle } from '../../helpers/theme'

interface ActionBarProps {
  fetchColumnLineage: typeof fetchColumnLineage
  depth: number
  setDepth: (depth: number) => void
}

export const ActionBar = ({ fetchColumnLineage, depth, setDepth }: ActionBarProps) => {
  const { namespace, name } = useParams()
  const navigate = useNavigate()
  const i18next = require('i18next')
  const [searchParams, setSearchParams] = useSearchParams()
  const { theme: iframeTheme } = useSelector((state: IState) => state.iframe)
  return (
    <Box
      sx={{
        borderBottomWidth: 2,
        borderTopWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderStyle: 'dashed',
      }}
      display={'flex'}
      height={HEADER_HEIGHT - 1}
      justifyContent={'space-between'}
      alignItems={'center'}
      px={2}
      borderColor={iframeTheme === 'dark' ? theme.palette.secondary.main : borderWhite}
    >
      <Box display={'flex'} alignItems={'center'}>
        <MQTooltip title={i18next.t('lineage.back_to_datasets')}>
          <IconButton size={'small'} sx={{ mr: 2, color: iframeTheme === 'dark' ? iframeLightColor : iframeLightBlockTitle }} onClick={() => navigate('/datasets')}>
            <ArrowBackIosRounded fontSize={'small'} />
          </IconButton>
        </MQTooltip>
        <MqText heading>{i18next.t('search.datasets')}</MqText>
        <Divider orientation='vertical' flexItem sx={{ mx: 2 }} />
        <Box>
          <MqText subdued>{i18next.t('lineage.mode')}</MqText>
          <MqText font={'mono'}>{i18next.t('lineage.column_level')}</MqText>
        </Box>
        <Divider orientation='vertical' flexItem sx={{ mx: 2 }} />
        <Box>
          <MqText subdued>{i18next.t('lineage.namespace')}</MqText>
          <MqText font={'mono'}>{namespace || 'Unknown namespace name'}</MqText>
        </Box>
        <Divider orientation='vertical' flexItem sx={{ mx: 2 }} />
        <Box>
          <MqText subdued>{i18next.t('lineage.name')}</MqText>
          <MqText font={'mono'}>{name || 'Unknown dataset name'}</MqText>
        </Box>
      </Box>
      <Box display={'flex'} alignItems={'center'}>
        <MQTooltip title={i18next.t('datasets_route.refresh')}>
          <IconButton
            sx={{ mr: 2 }}
            color={'primary'}
            size={'small'}
            onClick={() => {
              if (namespace && name) {
                fetchColumnLineage('DATASET', namespace, name, depth)
              }
            }}
          >
            <Refresh fontSize={'small'} />
          </IconButton>
        </MQTooltip>
        <TextField
          id='column-level-depth'
          type='number'
          inputProps={{ min: 0 }}
          label={i18next.t('lineage.graph_depth_title')}
          variant='outlined'
          size='small'
          sx={{ width: '80px',
            '& .MuiOutlinedInput-root': {
              color: iframeTheme === 'dark' ? iframeLightColor : theme.palette.secondary.main,
              '& fieldset': {
                borderColor: iframeTheme === 'dark' ? 'rgba(255,255,255,0.23)' : borderWhite,
              },
              '&:hover fieldset': {
                borderColor: iframeTheme === 'dark' ? iframeLightColor : '#ddd',
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              }
            },
            '& .MuiInputLabel-root': {
              color: iframeTheme === 'dark' ? 'rgba(171,177,189)' : theme.palette.secondary.main
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.palette.primary.main,
            },
           }}
          value={depth}
          onChange={(e) => {
            setDepth(isNaN(parseInt(e.target.value)) ? 0 : parseInt(e.target.value))
            searchParams.set('depth', e.target.value)
            setSearchParams(searchParams)
          }}
        />
      </Box>
    </Box>
  )
}
